import React, { useEffect, useState } from 'react';
import { playQuizVictorySound } from '../../utils/soundEffects';
import QuizResultsHost from './QuizResultsHost';

/**
 * QuizResults — Inkwell editorial podium on desktop, flat leaderboard on mobile.
 * Avatars dropped (they were redundant with the player name).
 *
 * Teachers (isHost) get a different layout — class summary + per-question
 * review, not a winner podium — see QuizResultsHost.
 */
function QuizResults({
  finalScores,
  playerId,
  isHost = false,
  questions = [],
  hostKeys = {},
  hostAnswers = {},
  gameType = 'text',
  onPlayAgain,
  onBackToMenu,
}) {
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // Skip the victory sound for the host — it's a teacher debrief, not a win.
    if (isHost) return;
    if (finalScores?.length > 0 && playerId === finalScores[0].playerId) {
      playQuizVictorySound();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isHost) {
    return (
      <QuizResultsHost
        finalScores={finalScores}
        questions={questions}
        hostKeys={hostKeys}
        hostAnswers={hostAnswers}
        gameType={gameType}
        onPlayAgain={onPlayAgain}
        onBackToMenu={onBackToMenu}
      />
    );
  }

  if (!finalScores || finalScores.length === 0) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--paper)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <h1 className="t-display" style={{ marginBottom: 24 }}>Game Over</h1>
          <button className="btn" onClick={onBackToMenu}>Back to Menu</button>
        </div>
      </div>
    );
  }

  const winner = finalScores[0];
  const isWinner = winner.playerId === playerId;
  // Visual podium order: 2nd | 1st | 3rd. Tag each slot with its true rank
  // so layoutIdx isn't misread as rank when fewer than 3 players finished
  // (e.g. solo run had the lone winner showing up as "二" before this fix).
  const podiumLayout = [
    finalScores[1] && { entry: finalScores[1], rank: 2 },
    finalScores[0] && { entry: finalScores[0], rank: 1 },
    finalScores[2] && { entry: finalScores[2], rank: 3 },
  ].filter(Boolean);
  const rest = finalScores.slice(3);

  const formatTime = (ms) => {
    if (!ms) return '--';
    const seconds = (ms / 1000).toFixed(1);
    return `${seconds}s`;
  };

  const heightFor = (rank) => (rank === 1 ? 220 : rank === 2 ? 180 : 150);
  const numeralFor = (rank) => (rank === 1 ? '一' : rank === 2 ? '二' : '三');

  return (
    <div style={{ minHeight: '100vh', background: 'var(--paper)' }}>
      <header className="ink-header">
        <span className="t-mono" style={{
          fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
        }}>
          Final results
        </span>
        <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          {finalScores.length} player{finalScores.length === 1 ? '' : 's'}
        </span>
      </header>

      <div style={{ maxWidth: 880, margin: '0 auto', padding: 'clamp(24px, 6vw, 48px) clamp(16px, 4vw, 32px)' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{
            fontFamily: 'var(--font-han)', fontSize: 'clamp(64px, 14vw, 96px)', lineHeight: 1,
            color: 'var(--vermillion)', marginBottom: 16,
          }}>
            {isWinner ? '赢' : '战'}
          </div>
          <h1 className="t-display" style={{ marginBottom: 8 }}>
            {isWinner ? 'You win!' : `${winner.playerName} wins`}
          </h1>
          <p className="t-body" style={{ fontSize: 17 }}>
            {winner.correct} out of {winner.total} correct
            {winner.finished && ` in ${formatTime(winner.finishMs)}`}.
          </p>
        </div>

        {isNarrow ? (
          /* Mobile: flat leaderboard list — no podium tiles, no avatars. */
          <div className="card" style={{ overflow: 'hidden', marginBottom: 32 }}>
            {finalScores.map((p, idx) => {
              const rank = idx + 1;
              const isMe = p.playerId === playerId;
              return (
                <div
                  key={p.playerId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: '16px 20px',
                    borderTop: idx ? '1px solid var(--rule-2)' : 'none',
                    background: isMe ? 'var(--paper-2)' : 'transparent',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-han)',
                      fontSize: 32,
                      lineHeight: 1,
                      color: rank <= 3 ? 'var(--vermillion)' : 'var(--ink-3)',
                      width: 36,
                      textAlign: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {rank <= 3 ? numeralFor(rank) : `#${rank}`}
                  </span>
                  <span style={{ flex: 1, fontWeight: 600 }}>
                    {p.playerName}{isMe && ' (you)'}
                  </span>
                  <span className="t-mono" style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                    {p.correct}/{p.total}
                  </span>
                  <span
                    className="t-mono"
                    style={{
                      fontSize: 13,
                      width: 56,
                      textAlign: 'right',
                      color: !p.finished ? 'var(--vermillion)' : 'var(--ink-3)',
                    }}
                  >
                    {p.finished ? formatTime(p.finishMs) : 'DNF'}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {/* Desktop: editorial podium 2|1|3 with hanzi numerals. */}
            <div className="qb-podium" style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
              gap: 16, alignItems: 'end', marginBottom: 32,
            }}>
              {podiumLayout.map(({ entry: p, rank: actualRank }) => {
                const isMe = p.playerId === playerId;
                return (
                  <div key={p.playerId} style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>
                      {p.playerName}{isMe && ' (you)'}
                    </div>
                    <div className="t-small" style={{ marginBottom: 12 }}>
                      {p.correct}/{p.total}{p.finished ? ` · ${formatTime(p.finishMs)}` : ' · DNF'}
                    </div>
                    <div style={{
                      height: heightFor(actualRank),
                      background: 'var(--paper-2)',
                      border: '1px solid var(--rule)',
                      borderRadius: '8px 8px 0 0',
                      color: 'var(--ink)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'var(--font-han)', fontSize: 64, fontWeight: 500,
                    }}>
                      {numeralFor(actualRank)}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop rest table for 4th place and below */}
            {rest.length > 0 && (
              <div className="card" style={{ overflow: 'hidden', marginBottom: 32 }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Player</th>
                      <th>Score</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rest.map((p, idx) => (
                      <tr key={p.playerId}>
                        <td className="t-mono">#{idx + 4}</td>
                        <td>{p.playerName}{p.playerId === playerId && ' (you)'}</td>
                        <td>{p.correct}/{p.total}</td>
                        <td className="t-mono" style={{ color: !p.finished ? 'var(--vermillion)' : 'inherit' }}>
                          {p.finished ? formatTime(p.finishMs) : 'DNF'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn--ghost" style={{ flex: 1 }} onClick={onBackToMenu}>
            Back to menu
          </button>
          <button className="btn btn--accent" style={{ flex: 2 }} onClick={onPlayAgain}>
            Play again →
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuizResults;
